import React, { useCallback } from 'react';
import { Backdrop, CircularProgress, Typography } from '@mui/material';
import { ConfigResponse, TermLink, TermLinksValue } from '../../store/modules/merchant/types';
import PageWrapper from '../../common/PageWrapper';
import { useConfigUpsert } from '../../hooks/useConfigUpsert';
import Logger from '../../util/logger';
import { useMerchant } from '../../contexts/MerchantContext';
import { useIsSkipifyAdmin } from '../../hooks';
import { ConfigSection } from '../../common';
import { TermLinkBox } from './components/TermLinkBox';
import { useGetConfigsQuery } from '../../store/modules/merchant/api';
import { toast } from '../../util/toast';

export const ShopperConsent = () => {
  const { merchantId, isLoading: merchantInfoLoading, canUpdateMerchantSettings } = useMerchant();
  const hasAdminAccess = useIsSkipifyAdmin();

  const {
    data: configs,
    isLoading: isLoadingConfigs,
    isFetching: isFetchingConfigs,
  } = useGetConfigsQuery(merchantId, {
    skip: !merchantId,
  });
  const termLinksConfig = configs?.find((c) => c.name === 'TERMS_LINKS') as
    ConfigResponse<TermLinksValue> | undefined;
  const { onConfigUpsert, isLoading: isSaving } = useConfigUpsert(merchantId);

  const handleTermLinkSubmit = useCallback(
    (termKey: keyof TermLinksValue, termLink: TermLink | undefined) =>
      onConfigUpsert({
        merchantId: termLinksConfig?.merchantId,
        configId: termLinksConfig?.configId,
        name: 'TERMS_LINKS',
        value: {
          ...termLinksConfig?.value,
          [termKey]: termLink,
        },
      }).catch((error) => {
        Logger.error(error);
        toast.error(
          'An error occurred while saving these changes. Please try again later or contact Skipify Support',
        );
      }),
    [
      onConfigUpsert,
      termLinksConfig?.configId,
      termLinksConfig?.merchantId,
      termLinksConfig?.value,
    ],
  );

  const hasModifyAccess = canUpdateMerchantSettings();
  const isFetchingUpdatedConfigs = isFetchingConfigs && !isLoadingConfigs;

  return (
    <PageWrapper
      showParentHierarchyBanner
      title="Shopper Consent"
      description="Use this page to add links to your policies for customers to see and visit during checkout."
      isLoading={merchantInfoLoading || isLoadingConfigs}
      key={configs?.[0]?.merchantId ?? merchantId}
    >
      <ConfigSection
        sx={{ pb: 3 }}
        title="Shopper Consent"
        description="You can submit up to three URLs that links to your policies to display to your customers. By completing their purchase, your customers will be agreeing to these policies."
      >
        <Typography sx={{ mt: 2, fontSize: '12px', color: 'grey.600', fontStyle: 'italic' }}>
          Note: Compliance approval for your policies is needed before they go live.
        </Typography>
        <TermLinkBox
          headerText="Purchase Terms and Conditions"
          inputLabel="Terms and Conditions URL"
          existingTermLink={termLinksConfig?.value?.termsAndConditions}
          onTermLinkSubmit={(termLink) => handleTermLinkSubmit('termsAndConditions', termLink)}
          hasAdminAccess={hasAdminAccess}
          readOnly={!hasModifyAccess}
        />
        <TermLinkBox
          headerText="Privacy Policy"
          inputLabel="Privacy Policy URL"
          existingTermLink={termLinksConfig?.value?.privacyPolicy}
          onTermLinkSubmit={(termLink) => handleTermLinkSubmit('privacyPolicy', termLink)}
          hasAdminAccess={hasAdminAccess}
          readOnly={!hasModifyAccess}
        />
        <TermLinkBox
          headerText="Terms of Use"
          inputLabel="Terms of Use URL"
          existingTermLink={termLinksConfig?.value?.termsOfUse}
          onTermLinkSubmit={(termLink) => handleTermLinkSubmit('termsOfUse', termLink)}
          hasAdminAccess={hasAdminAccess}
          readOnly={!hasModifyAccess}
        />
      </ConfigSection>
      <Backdrop
        sx={{ color: 'primary.dark', zIndex: 10 }}
        open={isSaving || isFetchingUpdatedConfigs}
      >
        <CircularProgress sx={{ left: '50%', position: 'absolute', top: '20%' }} />
      </Backdrop>
    </PageWrapper>
  );
};
